import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";
import WizardDomainStep from "./WizardDomainStep";
import DnsRecordTable from "./DnsRecordTable";
import RegistrarGuide from "./RegistrarGuide";
import { DomainProblems, DomainSteps, StateBadge } from "./DomainParts";
import { validHost } from "./helpers";

const INIT = { domain: "", primary: "www", variant: "registrar", prevalidate: false };
const STEPS = ["Dominio", "Record DNS", "Verifica e attiva"];

function Stepper({ step }) {
  return (
    <ol className="flex flex-wrap gap-2" data-testid="domain-wizard-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${i === step ? "bg-sky-600 text-white" : i < step ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`domain-wizard-step-${i + 1}`}>
          {i < step ? <Check size={12} /> : <span className="tabular-nums">{i + 1}</span>}{s}
        </li>
      ))}
    </ol>
  );
}

export default function DomainWizard({ open, onOpenChange, site, data, replacing, onData }) {
  const api = useApi();
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INIT);
  const [busy, setBusy] = useState(false);
  const [cost, setCost] = useState({ msg: "", ok: false });
  useEffect(() => { if (open) { setStep(0); setF(INIT); } }, [open]);
  useEffect(() => { setCost({ msg: "", ok: false }); }, [f.domain]);
  const doms = data?.domains || [];
  const prim = doms.find((d) => d.role === "primary");
  const connect = async () => {
    setBusy(true);
    try { const { data: res } = await api.post(`/sites/${site.id}/domains`, { ...f, confirm: true, replace: !!replacing, accept_cost: cost.ok }); onData(res); setStep(1); toast.success("Dominio collegato: ora crea i record DNS"); }
    catch (e) { if (e?.response?.status === 402) setCost({ msg: errMsg(e), ok: false }); else toast.error(errMsg(e)); }
    setBusy(false);
  };
  const verify = async () => {
    setBusy(true);
    try { const { data: res } = await api.post(`/sites/${site.id}/domains/check`); onData(res); setStep(2); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const spin = busy && <Loader2 size={14} className="mr-1.5 animate-spin" />;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto [&>*]:min-w-0" data-testid="domain-wizard">
        <DialogHeader>
          <DialogTitle>{replacing ? "Sostituisci il dominio" : "Collega il dominio del cliente"}</DialogTitle>
          <DialogDescription>HTTPS automatico con Cloudflare for SaaS. Il sito viene servito sul dominio solo dopo la verifica: prima nulla cambia.</DialogDescription>
        </DialogHeader>
        <Stepper step={step} />
        {step === 0 && (
          <div className="space-y-4">
            <WizardDomainStep f={f} setF={setF} />
            {replacing && prim && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="domain-wizard-replace-note">{prim.host} viene scollegato subito: finché il nuovo dominio non è attivo, il sito resta su {data.platform_domain}.</p>}
            <p className="text-xs text-slate-500" data-testid="domain-wizard-quota">Il dominio viene registrato sull'account {data?.mode === "emulator" ? "dell'emulatore locale" : "Cloudflare"}: {data?.quota.used} di {data?.quota.included} nomi inclusi già usati (gratis fino a {data?.quota.included}).</p>
            {cost.msg && <label className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"><Checkbox checked={cost.ok} onCheckedChange={(v) => setCost({ ...cost, ok: !!v })} className="mt-0.5" data-testid="domain-wizard-accept-cost" />{cost.msg}</label>}
            <div className="flex justify-end"><Button onClick={connect} disabled={busy || !validHost(f.domain) || (cost.msg && !cost.ok)} className="bg-sky-600 hover:bg-sky-700" data-testid="domain-wizard-connect">{spin}Collega dominio<ArrowRight size={14} className="ml-1.5" /></Button></div>
          </div>
        )}
        {step === 1 && prim && (
          <div className="space-y-4">
            <p className="text-sm text-slate-700">Crea questi record nella gestione DNS del dominio (presso il registrar del cliente). Clicca su nome o valore per copiarli.</p>
            {doms.map((d) => <div key={d.id} className="space-y-1.5"><p className="font-mono text-xs font-medium text-slate-600">{d.host}</p><DnsRecordTable records={d.records} apex={d.apex} testid={`domain-wizard-records-${d.role}`} /></div>)}
            <RegistrarGuide d={prim} target={data.cname_target} />
            <div className="flex justify-end"><Button onClick={verify} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="domain-wizard-verify">{spin}Ho creato i record: verifica<ArrowRight size={14} className="ml-1.5" /></Button></div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4" data-testid="domain-wizard-result">
            {doms.map((d) => (
              <div key={d.id} className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-sm font-medium">{d.host}</span><StateBadge d={d} testid={`domain-wizard-state-${d.role}`} /></div>
                <DomainSteps d={d} testid={`domain-wizard-steps-${d.role}`} /><DomainProblems d={d} testid={`domain-wizard-problems-${d.role}`} />
              </div>
            ))}
            <p className="text-xs text-slate-500">I DNS possono impiegare da pochi minuti ad alcune ore. Lo Studio continua a controllare da solo ogni minuto: puoi chiudere questa finestra.</p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={verify} disabled={busy} data-testid="domain-wizard-recheck">{busy ? spin : <RefreshCw size={14} className="mr-1.5" />}Verifica di nuovo</Button>
              <Button onClick={() => onOpenChange(false)} className="bg-sky-600 hover:bg-sky-700" data-testid="domain-wizard-done">Fatto</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
